// Global session tracking singleton (persists across component remounts)
// This prevents duplicate tracking caused by React StrictMode

// Initialize session ID immediately (module-level, persists across imports)
// Initialize from storage immediately (before any component renders)
let sessionId = null;
let isInitialized = false;

// Function to initialize session ID (called once)
function initializeSessionId() {
  if (isInitialized) {
    return sessionId;
  }
  
  try {
    const storageKey = 'blog_session_id';
    sessionId = sessionStorage.getItem(storageKey);
    if (sessionId) {
      window.__blogSessionId = sessionId; // Also store in memory
      console.log('🆔 [INIT] Initialized session ID from storage:', sessionId);
      isInitialized = true;
      return sessionId;
    } else if (window.__blogSessionId) {
      sessionId = window.__blogSessionId;
      console.log('🆔 [INIT] Initialized session ID from memory:', sessionId);
      isInitialized = true;
      return sessionId;
    }
  } catch (e) {
    console.warn('⚠️  Could not initialize session ID from storage:', e);
    if (window.__blogSessionId) {
      sessionId = window.__blogSessionId;
      console.log('🆔 [INIT] Initialized session ID from memory (fallback):', sessionId);
      isInitialized = true;
      return sessionId;
    }
  }
  
  isInitialized = true;
  return null;
}

// Initialize immediately
initializeSessionId();

const sessionTracking = {
  trackedBlogIds: new Set(),
  inProgress: new Set(), // Track blog IDs currently being tracked
  
  getSessionId() {
    // Return cached session ID if already initialized
    if (sessionId) {
      console.log('🆔 [getSessionId] Using cached session ID:', sessionId);
      return sessionId;
    }
    
    // Re-initialize if needed
    initializeSessionId();
    
    if (sessionId) {
      return sessionId;
    }
    
    const storageKey = 'blog_session_id';
    
    // Try to get from sessionStorage first
    let storedSessionId = null;
    try {
      storedSessionId = sessionStorage.getItem(storageKey);
    } catch (e) {
      console.warn('⚠️  sessionStorage not available:', e);
    }
    
    // Fallback to memory if sessionStorage fails
    if (!storedSessionId && window.__blogSessionId) {
      storedSessionId = window.__blogSessionId;
      console.log('🆔 [getSessionId] Using memory session ID (fallback):', storedSessionId);
    }
    
    if (!storedSessionId) {
      // Generate new session ID ONLY if we truly don't have one
      storedSessionId = crypto.randomUUID ? crypto.randomUUID() : 
        Date.now().toString(36) + Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2);
      
      console.log('🆔 [getSessionId] Generated NEW session ID:', storedSessionId);
      
      // Store immediately in both places
      try {
        sessionStorage.setItem(storageKey, storedSessionId);
        console.log('🆔 [getSessionId] Stored in sessionStorage');
      } catch (e) {
        console.warn('⚠️  Failed to store session ID in sessionStorage:', e);
      }
      
      // Always store in memory as backup
      window.__blogSessionId = storedSessionId;
      console.log('🆔 [getSessionId] Stored in memory');
    } else {
      console.log('🆔 [getSessionId] Using existing session ID:', storedSessionId);
      // Ensure it's also in memory
      window.__blogSessionId = storedSessionId;
    }
    
    // Cache it in module-level variable
    sessionId = storedSessionId;
    isInitialized = true;
    return sessionId;
  },
  
  isTracked(blogPostId) {
    const tracked = this.trackedBlogIds.has(blogPostId);
    if (tracked) {
      console.log('📋 Blog already tracked:', blogPostId);
    }
    return tracked;
  },
  
  markAsTracked(blogPostId) {
    this.trackedBlogIds.add(blogPostId);
    console.log('📋 Marked blog as tracked:', blogPostId);
    console.log('📋 Total tracked blogs:', this.trackedBlogIds.size);
  },
  
  isInProgress(blogPostId) {
    return this.inProgress.has(blogPostId);
  },
  
  markInProgress(blogPostId) {
    this.inProgress.add(blogPostId);
    console.log('⏳ Marked as in progress:', blogPostId);
  },
  
  markComplete(blogPostId) {
    this.inProgress.delete(blogPostId);
    console.log('✅ Marked as complete:', blogPostId);
  },
  
  reset() {
    sessionId = null;
    this.trackedBlogIds.clear();
    this.inProgress.clear();
  },
  
  // Get current session ID without generating
  getCurrentSessionId() {
    return sessionId || window.__blogSessionId || null;
  }
};

export default sessionTracking;

